import type { DictionaryPhrases } from '@/lib/dictPlugin';
import type { SitecoreDictionary } from '@/sitecoreState';

/**
 * Decides whether a JSS mini-app should hydrate the server's markup (createSSRApp) or render from
 * scratch over it (createApp).
 *
 * What `data-ssr` means, per createDomHtml in server/server.ts - it is the elapsed render time, or
 * String(ssr):
 *
 *   | server path         | data-ssr    | markup | correct behaviour   |
 *   | ------------------- | ----------- | ------ | ------------------- |
 *   | rendered            | "410"/"true"| real   | hydrate             |
 *   | enableSSR: false    | "false"     | empty  | client render       |
 *   | ssrOnly             | absent[1]   | real   | never mounted[1]    |
 *   | render threw        | "true"      | empty  | client render[2]    |
 *   | degraded[3]         | "410"/"true"| wrong  | client render       |
 *
 * [1] ssrOnly omits the whole __JSS_STATE__ script, so main.ts never builds an app for it.
 * [2] the state still carries ssrErrors, which is what hasSsrErrors keys off - hydrating an empty
 *     root would otherwise make Vue warn and fall back anyway.
 * [3] `data-degraded` on the same script: the render completed, but against data the server could
 *     not load - a dictionary that timed out, say - so its markup disagrees with what the client
 *     renders from the same page. Deliberately not routed through ssrErrors: that raises a toast and
 *     reaches Sentry, and a dictionary that is briefly cold after a deployment is not worth paging
 *     anyone about. The client renders over the markup instead, which costs a flash.
 */
export interface RehydrateInput {
  /** `data-ssr` from the component's __JSS_STATE__ script, or undefined when absent. */
  ssrAttribute?: string;
  isEditMode?: boolean;
  isShowroom?: boolean;
  hasSsrErrors?: boolean;
  /** APP_ENVIRONMENT - see HYDRATION_DISABLED_ENVIRONMENTS. */
  appEnvironment?: string;
  /** `data-degraded` - why the server's data was incomplete. Any value inhibits hydration. */
  degraded?: string;
  /** Why the markup is no longer the server's - see translatedDom.ts. Any value inhibits. */
  translated?: string;
}

/**
 * Hydration is switched off here for now. A mismatch on these environments is invisible - the
 * detailed warnings are compiled out (see __VUE_PROD_HYDRATION_MISMATCH_DETAILS__ in vite.config)
 * and all that is left is a console error with no location - while the cost of one is a subtree
 * that silently keeps the server's markup or loses its state. Client rendering over the server
 * markup costs a flash instead, which is what the site did until this work started.
 *
 * Lift this once TEST has been swept clean of mismatches: drop the environments from the list.
 */
const HYDRATION_DISABLED_ENVIRONMENTS = ['UAT', 'PROD'];

export const shouldRehydrate = ({
  ssrAttribute,
  isEditMode,
  isShowroom,
  hasSsrErrors,
  appEnvironment,
  degraded,
  translated,
}: RehydrateInput): boolean =>
  !HYDRATION_DISABLED_ENVIRONMENTS.includes(appEnvironment ?? '') &&
  !isShowroom &&
  !isEditMode &&
  !hasSsrErrors &&
  !degraded &&
  !translated &&
  // present and not the literal "false": the server produced markup worth hydrating
  !!ssrAttribute &&
  ssrAttribute !== 'false';

/**
 * The client's counterpart to degradedReason in server/degradedRender.ts, and the same verdict for
 * the same reason: with no phrases, every `$dict` key resolves to '' (see the dict plugin), so this
 * render disagrees with markup the server built from a dictionary it did have. Derived from what
 * setupDictionary returned - `{}` on every failure path - because data-degraded reports only what
 * went wrong on the *server*, and its reason is named apart from that one for the same reason.
 *
 * Takes the dictionary setting itself. script.jss-settings parses to the *site* object, so a
 * signature of SitecoreSettings reads `.site` off it, finds undefined and never reports anything -
 * and every all-optional interface here accepts every other one, so nothing types that mistake.
 *
 * This removes the mismatch, it does not rescue the page: rendering over the markup produces the
 * same empty labels a hydration repair would.
 */
export const clientDegradedReason = (
  dictionary: Partial<SitecoreDictionary> | null | undefined,
  phrases: DictionaryPhrases,
): string | undefined =>
  dictionary && !Object.keys(phrases).length ? 'client-dictionary' : undefined;
